import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { postApi } from '../../services/api';
import './Auth.css';

// Step constants
const STEP_EMAIL = 'email';
const STEP_RESET = 'reset';

const ForgotPassword = () => {
  const navigate = useNavigate();

  // Current step
  const [step, setStep] = useState(STEP_EMAIL);

  // Email step state
  const [emailInput, setEmailInput] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  // OTP & Reset Password step state
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [otpSentMsg, setOtpSentMsg] = useState('');

  // Success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const otpInputRefs = useRef([]);
  const cooldownRef = useRef(null);

  // Cleanup cooldown timer on unmount
  useEffect(() => () => clearInterval(cooldownRef.current), []);

  // ─────────────────────────────────────────────────────────────────
  // STEP 1 – Send OTP (verify-email API)
  // ─────────────────────────────────────────────────────────────────
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setEmailError('');
    setOtpSentMsg('');

    if (!emailInput.trim()) {
      setEmailError('Please enter your email address.');
      return;
    }

    setOtpLoading(true);
    try {
      const form = new FormData();
      form.append('email', emailInput.trim());

      const response = await postApi('verify-email', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.status) {
        setOtpSentMsg(response.data?.message || 'OTP has been sent to your email.');
        setStep(STEP_RESET);
        startCooldown();
      } else {
        setEmailError(response.data?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setEmailError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to send OTP. Please check your email and try again.'
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Cooldown timer ──────────────────────────────────────────────
  const startCooldown = () => {
    setResendCooldown(60);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setResetError('');
    setOtpSentMsg('');
    setOtpDigits(['', '', '', '', '', '']);

    try {
      const form = new FormData();
      form.append('email', emailInput.trim());

      const response = await postApi('verify-email', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.status) {
        setOtpSentMsg(response.data?.message || 'OTP has been resent to your email.');
        startCooldown();
      } else {
        setResetError(response.data?.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      console.error(err);
      setResetError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to resend OTP. Please try again.'
      );
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // OTP Input Helpers
  // ─────────────────────────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otpDigits];
    updated[index] = value;
    setOtpDigits(updated);
    setResetError('');

    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const updated = [...otpDigits];
    for (let i = 0; i < 6; i++) updated[i] = pasted[i] || '';
    setOtpDigits(updated);
    otpInputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  // ─────────────────────────────────────────────────────────────────
  // STEP 2 – Reset Password (password-reset API)
  // ─────────────────────────────────────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');

    const otpCode = otpDigits.join('');
    if (otpCode.length < 6) {
      setResetError('Please enter the complete 6-digit OTP.');
      return;
    }

    if (!password) {
      setResetError('Please enter a new password.');
      return;
    }

    if (password.length < 6) {
      setResetError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }

    setResetLoading(true);
    try {
      const form = new FormData();
      form.append('email', emailInput.trim());
      form.append('password', password);
      form.append('confirm_password', confirmPassword);
      form.append('otp', otpCode);

      const response = await postApi('password-reset', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.status) {
        const msg = response.data?.message || 'Password reset successfully!';
        setSuccessMessage(msg);
        setShowSuccessModal(true);
      } else {
        setResetError(response.data?.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setResetError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Password reset failed. Please check your details and try again.'
      );
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoToLogin = () => {
    setShowSuccessModal(false);
    navigate('/login');
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-bg">
        <div className="bg-animation"></div>
        <div className="floating-shapes">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="shape"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${20 + Math.random() * 10}s`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="container">
        <div className="row min-vh-100 align-items-center justify-content-center py-5">
          <div className="col-lg-5 col-md-7 col-11">
            <div className="auth-card glass-card">
              {/* Header */}
              <div className="auth-header">
                <Link to="/" className="auth-logo">
                  <h2>ANTIPSUN</h2>
                  <span className="logo-tag">CRYPTO</span>
                </Link>

                {step === STEP_EMAIL ? (
                  <>
                    <h3 className="auth-title">Forgot Password?</h3>
                    <p className="auth-subtitle">
                      Enter your email to receive a password reset OTP code
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="auth-title">Reset Password</h3>
                    <p className="auth-subtitle">
                      Enter the OTP sent to <span style={{ color: 'var(--accent-color)' }}>{emailInput}</span> and set your new password
                    </p>
                  </>
                )}

                {/* Progress indicator */}
                <div className="otp-step-indicator">
                  <div className={`otp-step-dot ${step === STEP_EMAIL || step === STEP_RESET ? 'active' : ''}`} />
                  <div className={`otp-step-line ${step === STEP_RESET ? 'active' : ''}`} />
                  <div className={`otp-step-dot ${step === STEP_RESET ? 'active' : ''}`} />
                </div>
              </div>

              {/* ════════════════════════════════════════════
                  STEP 1 – Email Input
              ════════════════════════════════════════════ */}
              {step === STEP_EMAIL && (
                <form onSubmit={handleRequestOtp} className="auth-form">
                  {emailError && (
                    <div className="alert alert-danger text-center py-2 mb-3">
                      {emailError}
                    </div>
                  )}

                  <div className="form-group mb-4">
                    <label className="form-label">
                      <i className="bi bi-envelope"></i> Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="Enter your registered email"
                      required
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-gradient w-100 mb-3"
                    disabled={otpLoading}
                  >
                    {otpLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Sending OTP…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        Send OTP
                      </>
                    )}
                  </button>

                  <div className="auth-footer">
                    <p>
                      Remember your password?{' '}
                      <Link to="/login" className="auth-link">
                        Back to Login
                      </Link>
                    </p>
                  </div>
                </form>
              )}

              {/* ════════════════════════════════════════════
                  STEP 2 – OTP & New Password Form
              ════════════════════════════════════════════ */}
              {step === STEP_RESET && (
                <form onSubmit={handleResetPassword} className="auth-form">
                  {otpSentMsg && (
                    <div className="alert otp-sent-alert text-center py-2 mb-3">
                      <i className="bi bi-check-circle me-2"></i>
                      {otpSentMsg}
                    </div>
                  )}

                  {resetError && (
                    <div className="alert alert-danger text-center py-2 mb-3">
                      {resetError}
                    </div>
                  )}

                  {/* Verified Email Field */}
                  <div className="form-group mb-3">
                    <label className="form-label">
                      <i className="bi bi-envelope"></i> Email Address
                    </label>
                    <div className="verified-email-field">
                      <input
                        type="email"
                        className="form-control"
                        value={emailInput}
                        readOnly
                      />
                      <span className="verified-badge">
                        <i className="bi bi-check-circle-fill"></i> Verified
                      </span>
                    </div>
                  </div>

                  {/* OTP Digits */}
                  <div className="form-group mb-3">
                    <label className="form-label">
                      <i className="bi bi-shield-lock"></i> 6-Digit OTP Code
                    </label>
                    <div className="otp-box-wrapper">
                      {otpDigits.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (otpInputRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className="otp-box"
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          onPaste={i === 0 ? handleOtpPaste : undefined}
                          autoFocus={i === 0}
                        />
                      ))}
                    </div>

                    <div className="otp-footer-links mt-2">
                      <button
                        type="button"
                        className="btn-text-link"
                        onClick={handleResendOtp}
                        disabled={resendCooldown > 0}
                      >
                        {resendCooldown > 0
                          ? `Resend OTP in ${resendCooldown}s`
                          : (
                            <>
                              <i className="bi bi-arrow-clockwise me-1"></i>
                              Resend OTP
                            </>
                          )}
                      </button>

                      <button
                        type="button"
                        className="btn-text-link"
                        onClick={() => {
                          setStep(STEP_EMAIL);
                          setOtpDigits(['', '', '', '', '', '']);
                          setResetError('');
                        }}
                      >
                        <i className="bi bi-pencil me-1"></i>
                        Change Email
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div className="form-group mb-3 position-relative">
                    <label className="form-label">
                      <i className="bi bi-lock"></i> New Password
                    </label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y me-3 cursor-pointer"
                      style={{ marginTop: '8px', cursor: 'pointer' }}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </span>
                  </div>

                  {/* Confirm Password */}
                  <div className="form-group mb-4 position-relative">
                    <label className="form-label">
                      <i className="bi bi-lock-fill"></i> Confirm New Password
                    </label>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y me-3 cursor-pointer"
                      style={{ marginTop: '8px', cursor: 'pointer' }}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </span>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-gradient w-100 mb-3"
                    disabled={resetLoading}
                  >
                    {resetLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Resetting Password…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Reset Password
                      </>
                    )}
                  </button>

                  <div className="auth-footer">
                    <p>
                      Back to{' '}
                      <Link to="/login" className="auth-link">
                        Login Screen
                      </Link>
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ====================== SUCCESS MODAL ====================== */}
      {showSuccessModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content glass-card border-success">
              <div className="modal-header border-0 text-center justify-content-center">
                <h5 className="modal-title text-success fw-bold">Password Reset Successful!</h5>
              </div>
              <div className="modal-body text-center">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3.5rem' }}></i>
                <p className="mt-3 mb-0 text-white">{successMessage}</p>
              </div>
              <div className="modal-footer border-0 justify-content-center">
                <button className="btn btn-gradient px-5" onClick={handleGoToLogin}>
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;
